// /src/engine/connectors.ts

import * as THREE from 'three';
import { findPartDef } from './partsCatalog';

export type ConnectorGender = 'M' | 'F';

export type ConnectorKind =
  | 'stud'
  | 'tube'
  | 'technicHole'
  | 'axleEnd'
  | 'axleHole'
  | 'ldcad';

export type ConnectorSource = 'procedural' | 'ldcad';

export type Connector = {
  kind: ConnectorKind;
  pos: THREE.Vector3; // local space
  dir: THREE.Vector3; // local space, normalized
  radius: number; // LDU
  id?: string;
  group?: string; // LDCad-style matching key
  gender?: ConnectorGender; // M/F
  source?: ConnectorSource;
  metaType?: string; // e.g. SNAP_CYL (optional)
};

const CACHE = new Map<string, Connector[]>();
const INFERRED_BY_PART = new Map<string, { version: number; connectors: Connector[] }>();
const INFERRED_CONNECTORS_CACHE_VERSION = 2;
const RESOLVED_CONNECTORS_CACHE_VERSION = 2;

function normalizePartRef(pf: string): string {
  const cleaned = String(pf ?? '')
    .replace(/\\/g, '/')
    .trim()
    .toLowerCase();
  if (!cleaned) return '';
  const base = cleaned.split('/').pop();
  return (base ?? cleaned).trim();
}

export function connectorsCompatible(a: Connector, b: Connector): boolean {
  const ag = (a.group ?? '').toLowerCase();
  const bg = (b.group ?? '').toLowerCase();
  if (ag && bg && a.gender && b.gender) {
    if (ag !== bg) return false;
    return a.gender !== b.gender; // opposite genders mate
  }

  // Fallback compatibility (legacy procedural kinds)
  const ak = a.kind;
  const bk = b.kind;

  if (ak === 'stud' && bk === 'tube') return true;
  if (ak === 'tube' && bk === 'stud') return true;

  if (ak === 'technicHole' && bk === 'axleEnd') return true;
  if (ak === 'axleEnd' && bk === 'technicHole') return true;

  if (ak === 'axleEnd' && bk === 'axleHole') return true;
  if (ak === 'axleHole' && bk === 'axleEnd') return true;

  return false;
}

export function getConnectorsForPart(partFile: string): Connector[] {
  const key = normalizePartRef(partFile);
  if (!key) return [];
  if (CACHE.has(key)) return CACHE.get(key)!;

  const def = findPartDef(partFile);
  if (!def) {
    CACHE.set(key, []);
    return [];
  }

  const studsW = def.studs.w;
  const studsD = def.studs.d;
  const plates = def.studs.plates;

  const w = studsW * 20;
  const d = studsD * 20;
  const h = plates * 8;

  const out: Connector[] = [];

  const isTile = def.category === 'Tiles';

  // Stud grid center positions match the fallback geometry.
  const studPositions: Array<{ x: number; z: number; idx: string }> = [];
  for (let x = 0; x < studsW; x++) {
    for (let z = 0; z < studsD; z++) {
      studPositions.push({
        x: -w / 2 + 10 + x * 20,
        z: -d / 2 + 10 + z * 20,
        idx: `${x},${z}`,
      });
    }
  }

  // "Stud" connectors on the top surface (y = h).
  if (!isTile) {
    for (const s of studPositions) {
      out.push({
        kind: 'stud',
        pos: new THREE.Vector3(s.x, h, s.z),
        dir: new THREE.Vector3(0, 1, 0),
        radius: 6.1,
        id: `stud:${s.idx}`,
        group: 'stud',
        gender: 'M',
        source: 'procedural',
      });
    }
  }

  // "Tube" connectors on the bottom surface (y = 0).
  // This approximation is good enough for stud↔tube snapping in a CAD editor.
  for (const s of studPositions) {
    out.push({
      kind: 'tube',
      pos: new THREE.Vector3(s.x, 0, s.z),
      dir: new THREE.Vector3(0, -1, 0),
      radius: 6.2,
      id: `tube:${s.idx}`,
      group: 'stud',
      gender: 'F',
      source: 'procedural',
    });
  }

  // Minimal technic support scaffold:
  // - Technic Brick 1x4 (holes) 3701.dat has three holes at stud centers along length.
  if (key === '3701.dat') {
    // This partDef in your catalog uses studs.w=1 d=4 -> length along Z.
    const y = h / 2;
    for (let z = 0; z < 4; z++) {
      const zPos = -d / 2 + 10 + z * 20;
      out.push({
        kind: 'technicHole',
        pos: new THREE.Vector3(0, y, zPos),
        dir: new THREE.Vector3(1, 0, 0), // hole axis approx X
        radius: 4.9,
        id: `hole:${z}`,
        group: 'technic',
        gender: 'F',
        source: 'procedural',
      });
    }
  }

  // Axle 4 scaffold (3705.dat) – two ends along X.
  if (key === '3705.dat') {
    out.push({
      kind: 'axleEnd',
      pos: new THREE.Vector3(-40, 4, 0),
      dir: new THREE.Vector3(-1, 0, 0),
      radius: 2.4,
      id: 'axle:endA',
      group: 'axle',
      gender: 'M',
      source: 'procedural',
    });
    out.push({
      kind: 'axleEnd',
      pos: new THREE.Vector3(40, 4, 0),
      dir: new THREE.Vector3(1, 0, 0),
      radius: 2.4,
      id: 'axle:endB',
      group: 'axle',
      gender: 'M',
      source: 'procedural',
    });
  }

  CACHE.set(key, out);
  return out;
}

const _q = new THREE.Quaternion();
const _tmpBounds = new THREE.Box3();
const _tmpMeshBounds = new THREE.Box3();
const _tmpInvRoot = new THREE.Matrix4();
const _tmpRel = new THREE.Matrix4();
const _tmpSize = new THREE.Vector3();
const _tmpCenter = new THREE.Vector3();

function inferBrickLikeConnectors(obj: THREE.Object3D): Connector[] {
  const partKey = normalizePartRef((obj.userData?.partFile as string | undefined) ?? '');
  if (partKey && INFERRED_BY_PART.has(partKey)) {
    const cached = INFERRED_BY_PART.get(partKey)!;
    if (cached.version === INFERRED_CONNECTORS_CACHE_VERSION) return cached.connectors;
  }

  _tmpBounds.makeEmpty();
  obj.updateMatrix();
  obj.updateMatrixWorld(true);
  _tmpInvRoot.copy(obj.matrixWorld).invert();

  let hasMesh = false;
  obj.traverse((n: THREE.Object3D) => {
    const m = n as THREE.Mesh;
    if (!m.isMesh) return;
    const g = m.geometry as THREE.BufferGeometry | undefined;
    if (!g) return;
    if (!g.boundingBox) g.computeBoundingBox();
    if (!g.boundingBox) return;
    _tmpMeshBounds.copy(g.boundingBox);
    _tmpRel.multiplyMatrices(_tmpInvRoot, m.matrixWorld);
    _tmpMeshBounds.applyMatrix4(_tmpRel);
    if (_tmpMeshBounds.isEmpty()) return;
    _tmpBounds.union(_tmpMeshBounds);
    hasMesh = true;
  });

  if (!hasMesh || _tmpBounds.isEmpty()) return [];

  _tmpBounds.getSize(_tmpSize);
  _tmpBounds.getCenter(_tmpCenter);
  if (_tmpSize.x <= 1e-4 || _tmpSize.y <= 1e-4 || _tmpSize.z <= 1e-4) return [];

  const studsW = Math.max(1, Math.round(_tmpSize.x / 20));
  const studsD = Math.max(1, Math.round(_tmpSize.z / 20));

  // Only infer for near-rectangular LEGO-like footprints.
  if (Math.abs(_tmpSize.x - studsW * 20) > 8 || Math.abs(_tmpSize.z - studsD * 20) > 8) {
    return [];
  }

  const heightStep = 8;
  // Use floor, not round, so the stud cap stays out of the body height.
  let bodyH = Math.floor((_tmpSize.y + 1e-4) / heightStep) * heightStep;
  bodyH = THREE.MathUtils.clamp(bodyH, heightStep, _tmpSize.y);
  const hasStudTop = (_tmpSize.y - bodyH) > 1.2;

  const bottomY = _tmpBounds.min.y;
  const topY = bottomY + bodyH;
  const startX = _tmpCenter.x - ((studsW - 1) * 20) / 2;
  const startZ = _tmpCenter.z - ((studsD - 1) * 20) / 2;

  const out: Connector[] = [];

  for (let x = 0; x < studsW; x++) {
    for (let z = 0; z < studsD; z++) {
      const px = startX + x * 20;
      const pz = startZ + z * 20;
      const idx = `${x},${z}`;

      out.push({
        kind: 'tube',
        pos: new THREE.Vector3(px, bottomY, pz),
        dir: new THREE.Vector3(0, -1, 0),
        radius: 6.2,
        id: `tube:${idx}`,
        group: 'stud',
        gender: 'F',
        source: 'procedural',
      });

      if (hasStudTop) {
        out.push({
          kind: 'stud',
          pos: new THREE.Vector3(px, topY, pz),
          dir: new THREE.Vector3(0, 1, 0),
          radius: 6.1,
          id: `stud:${idx}`,
          group: 'stud',
          gender: 'M',
          source: 'procedural',
        });
      }
    }
  }

  if (partKey && out.length) INFERRED_BY_PART.set(partKey, {
    version: INFERRED_CONNECTORS_CACHE_VERSION,
    connectors: out,
  });
  return out;
}

export function getConnectorWorldPos(obj: THREE.Object3D, c: Connector, out = new THREE.Vector3()) {
  // Support matrixAutoUpdate=false objects (TransformControls edits position/quaternion).
  obj.updateMatrix();
  obj.updateMatrixWorld(true);
  return out.copy(c.pos).applyMatrix4(obj.matrixWorld);
}

export function getConnectorWorldDir(obj: THREE.Object3D, c: Connector, out = new THREE.Vector3()) {
  obj.updateMatrix();
  obj.updateMatrixWorld(true);
  obj.getWorldQuaternion(_q);
  return out.copy(c.dir).applyQuaternion(_q).normalize();
}

export function getObjectConnectors(obj: THREE.Object3D): Connector[] {
  const baked = (obj.userData as any)?.connectors as Connector[] | undefined;
  if (Array.isArray(baked) && baked.length) return baked;

  const cached = (obj.userData as any)?.resolvedConnectors as Connector[] | undefined;
  const cachedVersion = Number((obj.userData as any)?.resolvedConnectorsVersion ?? 0);
  if (Array.isArray(cached) && cached.length && cachedVersion === RESOLVED_CONNECTORS_CACHE_VERSION) return cached;

  const pf = (obj.userData?.partFile as string | undefined) ?? '';
  const known = pf ? getConnectorsForPart(pf) : [];

  const inferred = inferBrickLikeConnectors(obj);
  if (known.length && inferred.length) {
    // Keep geometry-aligned stud/tube grid, but preserve any explicit technic/axle connectors.
    const extras = known.filter((c) => c.kind !== 'stud' && c.kind !== 'tube');
    const merged = extras.length ? [...inferred, ...extras] : inferred;
    (obj.userData as any).resolvedConnectors = merged;
    (obj.userData as any).resolvedConnectorsVersion = RESOLVED_CONNECTORS_CACHE_VERSION;
    return merged;
  }

  if (known.length) {
    (obj.userData as any).resolvedConnectors = known;
    (obj.userData as any).resolvedConnectorsVersion = RESOLVED_CONNECTORS_CACHE_VERSION;
    return known;
  }

  if (inferred.length) {
    (obj.userData as any).resolvedConnectors = inferred;
    (obj.userData as any).resolvedConnectorsVersion = RESOLVED_CONNECTORS_CACHE_VERSION;
    return inferred;
  }

  return [];
}
